/**
 * The next week of a schedule, drawn: a tick for each run, and under the first
 * few the stretch of data each one reads.
 *
 * The runs are placed exactly as `backend/argus_schedule.py` places them -
 * `every` slots from the Unix epoch, shifted by `at`, and by four days for a
 * weekly rule so it falls on a Monday. Nothing is evaluated: it is arithmetic
 * on the three picks, so it can follow them before Apply.
 */

import { svg, html, type TemplateResult } from 'lit';
import type { ArgusSchedule } from '../../api/types.ts';
import { durationSeconds } from './argus-schedule-panel.ts';

const DAY = 86400;
const MONDAY = 4 * DAY;

function offset(s: ArgusSchedule): number {
  let out = 0;
  if (s.at) {
    const [h, m] = s.at.split(':');
    out = Number(h) * 3600 + Number(m) * 60;
  }
  if (durationSeconds(s.every) >= 7 * DAY) out += MONDAY;
  return out;
}

/** Run times (epoch seconds) in [from, to), on the watcher's grid. */
export function runsBetween(s: ArgusSchedule, from: number, to: number): number[] {
  const every = durationSeconds(s.every);
  if (!every) return [];
  const off = offset(s);
  const out: number[] = [];
  let t = Math.ceil((from - off) / every) * every + off;
  for (; t < to && out.length < 2000; t += every) out.push(t);
  return out;
}

const LEFT = 44;
const BANDS = 4;
/** Drawn at the width it is given, in pixels, so text stays the page's size
 *  however wide the zone is; 700 until it has been measured. */
const DEFAULT_WIDTH = 700;

export function renderSchedulePreview(
  s: ArgusSchedule,
  now = Date.now() / 1000,
  width = DEFAULT_WIDTH,
): TemplateResult {
  const W = Math.max(320, Math.round(width));
  const RIGHT = W - 8;
  const lookback = durationSeconds(s.lookback);
  // Far enough back that the first run's reading shows whole - up to a week;
  // a longer one runs off the left edge, marked.
  const from = Math.floor((now - Math.min(lookback, 7 * DAY)) / DAY) * DAY;
  const to = Math.floor(now / DAY) * DAY + 7 * DAY;
  const runs = runsBetween(s, now, to);
  const dense = runs.length > 60;
  const x = (t: number): number => LEFT + ((t - from) / (to - from)) * (RIGHT - LEFT);
  const span = Math.round((to - from) / DAY);
  const step = span > 10 ? 2 : 1;
  const days = Array.from({ length: span + 1 }, (_, i) => from + i * DAY);
  // With room to spare, the day is cut into quarters: fine lines at 06, 12
  // and 18 UTC, labelled once a day is wide enough to read them.
  const perDay = (RIGHT - LEFT) / span;
  const quarters = perDay >= 90 ? days.slice(0, -1).flatMap((d) => [6, 12, 18].map((h) => d + h * 3600)) : [];
  const hourLabels = perDay >= 140;
  const label = (t: number): string =>
    new Date(t * 1000).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
  const bandRuns = runs.slice(0, BANDS);
  const height = 44 + bandRuns.length * 11 + 18;
  return html`<figure class="argus-sched-preview">
    <figcaption class="argus-sched-label">
      Runs in the next 7 days, UTC · ${runs.length.toLocaleString()} run${runs.length === 1 ? '' : 's'}${dense
        ? ` - too close together to tell apart here`
        : ''}
    </figcaption>
    <svg viewBox=${`0 0 ${W} ${height}`} role="img" aria-label=${`${runs.length} runs in the next 7 days`}>
      ${days.map(
        (d, i) => svg`<line class="grid" x1=${x(d)} x2=${x(d)} y1="6" y2=${height - 16}></line>
          ${i < span && i % step === 0 ? svg`<text class="day" x=${x(d) + 3} y=${height - 4}>${label(d)}</text>` : ''}`,
      )}
      ${quarters.map(
        (q) => svg`<line class="grid is-fine" x1=${x(q)} x2=${x(q)} y1="6" y2=${height - 16}></line>
          ${hourLabels ? svg`<text class="hour" x=${x(q) + 2} y=${height - 4}>${String(new Date(q * 1000).getUTCHours()).padStart(2, '0')}</text>` : ''}`,
      )}
      <line class="now" x1=${x(now)} x2=${x(now)} y1="4" y2=${height - 16}></line>
      <text class="lane" x="0" y="20">runs</text>
      ${runs.map((t) => svg`<line class="run ${dense ? 'is-dense' : ''}" x1=${x(t)} x2=${x(t)} y1="10" y2="28"></line>`)}
      ${bandRuns.length ? svg`<text class="lane" x="0" y="46">reads</text>` : ''}
      ${bandRuns.map((t, i) => {
        const cut = x(t - lookback) < LEFT;
        const a = Math.max(LEFT, x(t - lookback));
        return svg`<rect class="read" x=${a} y=${38 + i * 11} width=${Math.max(1, x(t) - a)} height="7" rx="1"></rect>
          ${cut ? svg`<text class="cut" x=${LEFT - 2} y=${44 + i * 11} text-anchor="end">‹</text>` : ''}`;
      })}
    </svg>
    <p class="argus-lfaint">
      Each tick is a run; under the first ${bandRuns.length === 1 ? 'one' : bandRuns.length} is the data it reads.
      Where they overlap an episode is seen again and kept as one.
    </p>
  </figure>`;
}
